import styled, { DefaultTheme, css } from 'styled-components';

type WrapperProps = {
  variant:
  | 'primary'
  | 'secondary'
  | 'success'
  | 'minimal'
  | 'text'
  | 'anchor'
  | 'invert';
  fullWidth: boolean;
  hasIcon: boolean;
  iconPosition: 'right' | 'left';
  size: 'small' | 'medium' | 'default';
  status?: 'info' | 'success';
  styleVariant?: 'filled' | 'outline';
};

const WrapperModifiers = {
  small: () => css`
    padding: 6px 12px;
    font-size: 1.4rem;
  `,
  medium: () => css`
    padding: 8px 16px;
    font-size: 1.6rem;
  `,
  default: () => css`
    padding: 10px 24px;
    font-size: 1.8rem;
  `,
  fullWidth: () => css`
    display: flex;
    justify-content: center;
    width: 100%;
  `,
  withIcon: (iconPosition: string) => css`
    svg,
    img {
      order: ${iconPosition === 'left' ? 1 : 2};
      margin: ${iconPosition === 'left' ? '0 8px 0 0' : '0 0 0 8px'};

      & + span {
        order: ${iconPosition === 'left' ? 2 : 1};
      }
    }
  `,
  primary: (theme: DefaultTheme) => css`
    background-color: ${theme.colors.brandPrimary70};
    border: none;
    color: ${theme.colors.globalWhite100};

    &:hover {
      background-color: ${theme.colors.purple80};
      color: ${theme.colors.globalWhite100};
    }

    &:active,
    &:focus {
      outline: 2px solid ${theme.colors.brandPrimary30};
      outline-offset: 1px;
    }
  `,
  brandPrimary: (theme: DefaultTheme) => css`
    background-color: ${theme.colors.brandSecondary80};
    background: linear-gradient(270deg, ${theme.colors.brandSecondary80} 0%, ${theme.colors.brandPrimary70} 100%);
    border: none;
    color: ${theme.colors.globalWhite100};

    &:hover {
      background-color: ${theme.colors.purple80};
      color: ${theme.colors.globalWhite100};
      background: linear-gradient(270deg, ${theme.colors.brandPrimary70} 0%, ${theme.colors.brandSecondary80} 100%);
    }

    &:active,
    &:focus {
      outline: 2px solid ${theme.colors.brandPrimary30};
      background: linear-gradient(270deg, ${theme.colors.brandPrimary70} 0%, ${theme.colors.brandSecondary80} 100%);
      outline-offset: 1px;
    }
  `,
  text: (theme: DefaultTheme, styleVariant: WrapperProps['styleVariant']) => css`
    ${styleVariant === 'outline' && css`
      background-color: ${theme.colors.globalWhite100};
      border: 1px solid ${theme.colors.neutralSilver10};
      color: ${theme.colors.neutralSilver80};

      &:hover {
        background-color: ${theme.colors.neutralSilver5};
        border: 1px solid ${theme.colors.neutralSilver10};
        color: ${theme.colors.neutralSilver80};
      }

      &:active,
      &:focus {
        background-color: ${theme.colors.neutralSilver10};
        color: ${theme.colors.neutralSilver90};
        outline: 2px solid ${theme.colors.neutralSilver20};
        outline-offset: 1px;
      }
    `}

    ${styleVariant === 'filled' && css`
      background-color: ${theme.colors.neutralSilver5};
      border: 0px solid ${theme.colors.neutralSilver10};
      color: ${theme.colors.neutralSilver80};

      &:hover {
        background-color: ${theme.colors.neutralSilver5};
        border: 1px solid ${theme.colors.neutralSilver10};
        color: ${theme.colors.neutralSilver80};
      }

      &:active,
      &:focus {
        background-color: ${theme.colors.brandPrimary20};
        border: 1px solid ${theme.colors.brandPrimary100};
        color: ${theme.colors.brandPrimary100};
        outline: 2px solid ${theme.colors.brandPrimary30};
        outline-offset: 1px;
      }
    `}

  `,
  secondary: (theme: DefaultTheme) => css`
    background-color: ${theme.colors.globalWhite100};
    border: 1px solid ${theme.colors.brandPrimary70};
    color: ${theme.colors.brandPrimary70};

    &:hover {
      background-color: ${theme.colors.brandPrimary10};
      border: 1px solid ${theme.colors.purple80};
      color: ${theme.colors.purple80};
    }

    &:active,
    &:focus {
      background-color: ${theme.colors.brandPrimary20};
      border: 1px solid ${theme.colors.brandPrimary100};
      color: ${theme.colors.brandPrimary100};
      outline: 2px solid ${theme.colors.brandPrimary30};
      outline-offset: 1px;
    }
  `,
  success: (theme: DefaultTheme) => css`
    background-color: ${theme.colors.statusSuccess30};
    border: none;
    color: ${theme.colors.globalWhite100};

    &:hover {
      background-color: ${theme.colors.statusSuccess40};
      color: ${theme.colors.globalWhite100};
    }

    &:active,
    &:focus {
      background-color: ${theme.colors.statusSuccess50};
      color: ${theme.colors.globalWhite100};
      outline: 2px solid ${theme.colors.statusSuccess20};
      outline-offset: 1px;
    }
  `,
  minimal: (theme: DefaultTheme) => css`
    background-color: transparent;
    border: none;
    color: ${theme.colors.neutralSilver60};

    &:hover {
      background-color: ${theme.colors.neutralSilver5};
      color: ${theme.colors.neutralSilver80};
      border-radius: 4px;
    }

    &:active,
    &:focus {
      background-color: ${theme.colors.neutralSilver20};
      color: ${theme.colors.neutralSilver100};
      border-radius: 4px;
      outline: 2px solid ${theme.colors.neutralSilver20};
      outline-offset: 1px;
    }
  `,
  anchor: (theme: DefaultTheme) => css`
    background-color: transparent;
    border: 0;
    border-bottom: 1px solid ${theme.colors.purple80};
    border-radius: 0;
    color: ${theme.colors.purple80};
    padding: 0 !important;

    &:hover {
      color: ${theme.colors.purple80};
    }
  `,
  invert: (theme: DefaultTheme) => css`
    background-color: ${theme.colors.globalWhite100};
    border: 1px solid ${theme.colors.statusSuccess30};
    color: ${theme.colors.statusSuccess30};

    &:hover {
      background-color: ${theme.colors.statusSuccess10};
      border: 1px solid ${theme.colors.statusSuccess40};
      color: ${theme.colors.statusSuccess40};
    }

    &:active,
    &:focus {
      background-color: ${theme.colors.statusSuccess10};
      border: 1px solid ${theme.colors.statusSuccess50};
      color: ${theme.colors.statusSuccess50};
      outline: 2px solid ${theme.colors.statusSuccess20};
      outline-offset: 1px;
    }
  `,
  info: (theme: DefaultTheme) => css`
    background-color: ${theme.colors.brandPrimary20};
    color: ${theme.colors.brandPrimary100};

    &:hover {
      background-color: ${theme.colors.brandPrimary30};
      color: ${theme.colors.globalWhite100};
    }

    &:active,
    &:focus {
      outline: 2px solid ${theme.colors.brandPrimary30};
      outline-offset: 1px;
    }
  `,
  done: (theme: DefaultTheme) => css`
    background-color: ${theme.colors.statusSuccess10};
    color: ${theme.colors.statusSuccess50};

    &:hover {
      background-color: ${theme.colors.statusSuccess20};
      color: ${theme.colors.globalWhite100};
    }

    &:active,
    &:focus {
      outline: 2px solid ${theme.colors.statusSuccess40};
      outline-offset: 1px;
    }
  `,
};

export const Wrapper = styled.button<WrapperProps>`
  ${({ theme, variant, fullWidth, size, hasIcon, iconPosition, status, styleVariant }) => css`
    border-radius: 4px;
    font-weight: 500;
    line-height: 1.6;
    display: inline-flex;
    align-items: center;
    text-decoration: none;
    cursor: pointer;

    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }

    ${variant && WrapperModifiers[variant](theme, styleVariant)}
    ${size && WrapperModifiers[size]()}
    ${fullWidth && WrapperModifiers.fullWidth()}
    ${hasIcon && WrapperModifiers.withIcon(iconPosition)}
    ${status === 'info' && WrapperModifiers.info(theme)}
    ${status === 'success' && WrapperModifiers.done(theme)}
  `}
`;
